import React, { createContext, useContext, useState, useCallback } from 'react';
import enLocale from '../locales/en.json';
import esLocale from '../locales/es.json';
import ptLocale from '../locales/pt.json';
import frLocale from '../locales/fr.json';

/**
 * Bundled locale map.
 * Keys are BCP-47 language codes as displayed in the language selector.
 * Add new locale JSON imports here when ready.
 */
const LOCALE_MAP = {
    English:    enLocale,
    Spanish:    esLocale,
    Portuguese: ptLocale,
    French:     frLocale,
};

const DEFAULT_LOCALE = 'English';

const ConstantsContext = createContext();

/**
 * Flattens all namespace sections of a locale into a single object.
 * This preserves backward compatibility — existing components using
 * useConstants().MY_APPS etc. continue to work without changes.
 *
 * Key precedence (last wins):  global → navigation → chat → myApps → ...
 * Page-specific namespaces are ALSO exposed directly so new code can do:
 *   const { chat, myApps } = useConstants();
 *   chat.GENIE_START_PROMPT
 */
    const flattenLocale = (localeData) => {
    const {
        global = {},
        navigation = {},
        chat = {},
        myApps = {},
        procurementChat = {},
        translate = {},
        gmtChat = {},
        help = {},
        auth = {},
        decrypt = {},
        videoTutorials = {},
        talentPulse360 = {},
        deepInsights = {},
    } = localeData;

    return {
        // ── Backward-compat flat keys ──────────────────────────────────────
        ...global,
        ...navigation,
        ...chat,
        ...myApps,
        ...procurementChat,
        ...translate,
        ...gmtChat,
        ...help,
        ...auth,
        ...decrypt,
        ...videoTutorials,
        ...talentPulse360,
        ...deepInsights,

        // ── Page namespaces (selective / new usage) ────────────────────────
        global,
        navigation,
        chat,
        myApps,
        procurementChat,
        translate,
        gmtChat,
        help,
        auth,
        decrypt,
        videoTutorials,
        talentPulse360,
        deepInsights,
    };
};

const BCP47_MAP = {
    English:    'en-US',
    Spanish:    'es-ES',
    Portuguese: 'pt-BR',
    French:     'fr-FR',
};

export const ConstantsProvider = ({ children }) => {
    const [locale, setLocale] = useState(DEFAULT_LOCALE);
    const [constants, setConstants] = useState(flattenLocale(LOCALE_MAP[DEFAULT_LOCALE]));

    /**
     * Switch the active locale.
     *
     * Phase 1 (current):  loads from the bundled LOCALE_MAP.
     * Phase 2 (future):   uncomment the API block and remove the LOCALE_MAP
     *                     fallback once the backend endpoint is ready.
     *
     * @param {string} lang - Language label matching the selector (e.g. 'English', 'Spanish')
     */
    const changeLocale = useCallback(async (lang) => {
        /* ── Phase 2: API-driven translations ─────────────────────────────
        try {
            const res = await fetch(`/api/translations?lang=${lang}`);
            if (!res.ok) throw new Error('Translation fetch failed');
            const localeData = await res.json();
            setConstants(flattenLocale(localeData));
            setLocale(lang);
            return;
        } catch (err) {
            console.warn(`[i18n] Could not fetch translations for "${lang}", falling back to static.`, err);
        }
        ── end Phase 2 ────────────────────────────────────────────────── */

        // Phase 1 — static bundled map
        const localeData = LOCALE_MAP[lang] ?? LOCALE_MAP[DEFAULT_LOCALE];
        setConstants(flattenLocale(localeData));
        setLocale(lang);
    }, []);

    const value = {
        ...constants,
        locale,
        localeCode: BCP47_MAP[locale] ?? BCP47_MAP[DEFAULT_LOCALE],
        changeLocale,
    };

    return (
        <ConstantsContext.Provider value={value}>
            {children}
        </ConstantsContext.Provider>
    );
};

export const useConstants = () => useContext(ConstantsContext);
